/**
 * The customer's own wall, with their canvas on it. PLAN-3D.md §7.6.
 *
 * Flat, on purpose, and this is the one place in Trimbox where 2D beats 3D.
 *
 * A photograph carries its own perspective, focal length and lighting, fixed at
 * the moment it was taken. Standing a 3D canvas in front of it means viewing
 * that photograph from an angle it was never taken at, and putting the canvas
 * in a perspective that disagrees with the room behind it. That disagreement is
 * exactly what makes a mockup look fake. A flat composite reads as real because
 * it does not pretend to know the geometry.
 *
 * What it cannot know is scale: the canvas is 1200 mm, the wall is however wide
 * it is. So it asks. The customer marks two points on something they know the
 * size of and says what it measures, and everything after that is arithmetic.
 * Guessing a standard wall would be one click and wrong often enough to sell
 * somebody the wrong size.
 */

import { useRef, useState } from 'react';
import type { ThreeConfig } from '../three/types';
import { CanvasPanels, useCanvasArt, wholeWidth } from './CanvasArt';

/** A point on the photograph, as a fraction of its width and height. */
interface Spot {
  x: number;
  y: number;
}

export function WallPhoto( { config, photo }: { config: ThreeConfig; photo: HTMLImageElement } ) {
  const canvas = config.canvas!;
  const frame = useRef<HTMLDivElement>( null );

  const [ marks, setMarks ] = useState<Spot[]>( [] );
  const [ known, setKnown ] = useState( '' );
  const [ at, setAt ] = useState<Spot>( { x: 0.5, y: 0.42 } );
  // The design as the picture that goes on the wall - shared with RoomView.
  const art = useCanvasArt();

  /** Where a pointer landed, as a fraction of the photograph. */
  const spotFrom = ( event: { clientX: number; clientY: number } ): Spot | null => {
    const box = frame.current?.getBoundingClientRect();

    if ( ! box ) return null;

    return { x: ( event.clientX - box.left ) / box.width, y: ( event.clientY - box.top ) / box.height };
  };

  const measured = marks.length === 2 && Number( known ) > 0;

  /*
   * How wide the canvas is, as a fraction of the photograph's width.
   *
   * The two marks span a known number of millimetres, which says how many
   * millimetres the whole photograph is across; the canvas is then simply its
   * own width against that. Measured on the diagonal, so the customer can mark
   * the corners of a door rather than hunting for two points on one line.
   */
  const wide = ( () => {
    if ( ! measured ) return 0.35;

    const box = frame.current?.getBoundingClientRect();
    const ratio = box ? box.height / box.width : 1;
    const span = Math.hypot( marks[ 1 ]!.x - marks[ 0 ]!.x, ( marks[ 1 ]!.y - marks[ 0 ]!.y ) * ratio );

    if ( span < 0.01 ) return 0.35;

    const acrossMm = Number( known ) * 10 / span; // The photograph's width, in mm.

    return Math.min( 0.98, wholeWidth( canvas ) / acrossMm );
  } )();

  return (
    <div className="tb-wall">
      <div
        className="tb-wall__frame"
        ref={ frame }
        onClick={ ( event ) => {
          if ( measured ) return;

          const spot = spotFrom( event );

          if ( spot ) setMarks( ( was ) => ( was.length >= 2 ? [ spot ] : [ ...was, spot ] ) );
        } }
      >
        <img src={ photo.src } alt="" className="tb-wall__photo" />

        { /* The marks stay after measuring, so what was measured is still visible. */ }
        { marks.map( ( mark, index ) => (
          <span key={ index } className="tb-wall__mark" style={ { left: `${ mark.x * 100 }%`, top: `${ mark.y * 100 }%` } } />
        ) ) }

        { marks.length === 2 && (
          <svg className="tb-wall__line" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            <line x1={ marks[ 0 ]!.x * 100 } y1={ marks[ 0 ]!.y * 100 } x2={ marks[ 1 ]!.x * 100 } y2={ marks[ 1 ]!.y * 100 } />
          </svg>
        ) }

        { measured && (
          <div
            className="tb-wall__art"
            style={ { left: `${ at.x * 100 }%`, top: `${ at.y * 100 }%`, width: `${ wide * 100 }%` } }
            onPointerDown={ ( event ) => {
              event.preventDefault();
              ( event.target as HTMLElement ).setPointerCapture( event.pointerId );

              const move = ( to: PointerEvent ) => {
                const spot = spotFrom( to );

                if ( spot ) setAt( { x: Math.min( 1, Math.max( 0, spot.x ) ), y: Math.min( 1, Math.max( 0, spot.y ) ) } );
              };

              const done = () => {
                window.removeEventListener( 'pointermove', move );
                window.removeEventListener( 'pointerup', done );
              };

              window.addEventListener( 'pointermove', move );
              window.addEventListener( 'pointerup', done );
            } }
          >
            <CanvasPanels canvas={ canvas } art={ art } />
          </div>
        ) }

        { /*
           * What to do next, on the photograph rather than under it.
           *
           * Under it, the step being asked for was a line of small grey text
           * away from the thing it was about - and the row it appeared in
           * changed the height of the frame, which moved the marks against the
           * photograph and put the scale out by a few per cent. On the
           * photograph, the frame never changes size at all.
           */ }
        { marks.length < 2 && (
          <div className="tb-wall__hint tb-wall__hint--tip">
            <strong>Set the scale</strong>
            <span>
              Click two points on something you can measure — the width of a door, a sofa, or a window.
            </span>
          </div>
        ) }

        { /*
           * The field stays once the measurement has been given.
           *
           * It used to disappear as soon as it held a number, which meant
           * typing "200" left it holding "2" - the photograph was told the door
           * was two centimetres and the canvas filled the wall. A field that
           * vanishes while somebody is still typing into it is never right, so
           * this is keyed on there being two marks rather than on the answer
           * being usable.
           */ }
        { marks.length === 2 && (
          <div
            className="tb-wall__hint tb-wall__hint--ask"
            /* The frame turns a click into a mark; inside here it must not. */
            onClick={ ( event ) => event.stopPropagation() }
          >
            <strong>
              { measured ? 'Drag the picture to where it will hang' : 'How far apart are those two points?' }
            </strong>

            <div className="tb-wall__row">
              <input
                id="tb-wall-apart"
                type="number"
                min="1"
                step="1"
                value={ known }
                autoFocus
                placeholder="80"
                onChange={ ( event ) => setKnown( event.target.value ) }
                aria-label="Distance in centimetres"
              />
              <span>cm apart</span>
              <button
                type="button"
                className="tb-wall__again"
                onClick={ () => { setMarks( [] ); setKnown( '' ); } }
              >
                Mark again
              </button>
            </div>
          </div>
        ) }
      </div>
    </div>
  );
}
